<template>
  <div class="d-flex flex-column" ref="containerRef">
    <section class="w-100 top-section pt-5">
      <CloseButton @on-click="onClose" class="text-black mt-3" />
      <div class="d-flex flex-column align-items-center mx-auto w-100 px-3">
        <img
          :src="useAsset('img/credit-line/card_coin_sparkle.svg')"
          alt="card coins"
        />
        <h1 class="text-center mt-1">{{ t("CREDIT_LIMIT_INCREASE.TITLE") }}</h1>
        <h2 class="text-center px-4">
          {{ t("CREDIT_LIMIT_INCREASE.UNLOCK_CARD_WITH") }}
        </h2>

        <div class="text-center w-100">
          <span class="text-center new-amount">{{ newCreditLimit }}</span>
        </div>
      </div>
    </section>

    <ContractAcceptance
      ref="contractAcceptanceRef"
      :show-marketing-communications="false"
      @signature-drawn="disableAcceptBtn = false"
      @signature-cleared="disableAcceptBtn = true"
      @open-document="openDocument"
    />

    <section
      class="w-100 px-4 d-flex justify-content-center flex-column pb-3 mb-0 mt-auto position-relative bottom-0"
    >
      <Button
        @click="acceptLimitIncrease"
        :loading="loading"
        type="primary"
        :disabled="disableAcceptBtn"
      >
        {{ t("ACCEPT") }}
      </Button>
    </section>
  </div>

  <!--////////////MODAL DOCUMENTS//////////-->
  <Modal :show="showModal" show-close-button @on-close="showModal = false">
    <template #header>
      <div
        class="d-flex flex-row justify-content-between align-items-center w-100"
      >
        <h5 class="text-black fw-bolder text-start">
          {{ docModalTitle }}
        </h5>
      </div>
    </template>
    <template #body class="mt-2">
      <iframe
        id="modal-body-iframe"
        class="content-iframe"
        :srcdoc="modalContent"
      ></iframe>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { DocFactoryService } from "@/apps/credit-line/services/multi-country/doc-factory.service";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import CloseButton from "@/shared-components/consumer/CloseButton.vue";
import { useNewLimitIncreaseOffer } from "@/apps/credit-line/hooks/useNewLimitIncreaseOffer";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { onMounted, ref } from "vue";
import {
  getWindowHeightPercentage,
  resizeComponentHelper,
} from "@/apps/credit-line/helpers/resizeComponent.helper";
import Button from "@/apps/credit-line/components/Button.vue";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { Service as ApiService } from "@/apps/credit-line/services/network.service";
import { useRouter } from "vue-router";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import ContractAcceptance from "@/apps/credit-line/components/Offers/ContractAcceptance.vue";
import { IContractData } from "@/apps/credit-line/models/IContractData";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import Modal from "@/apps/credit-line/components/Modal.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import percentageFormat from "@/apps/credit-line/helpers/percentageFormatter.helper";
import { getContractDate } from "@/apps/credit-line/helpers/registrations.helper";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";

const { t } = useI18n();
const router = useRouter();
const containerRef = ref();
const { newCreditLimit } = useNewLimitIncreaseOffer();
const { summary, fetchSummary } = useCreditLineSummary();
const contractAcceptanceRef = ref<InstanceType<typeof ContractAcceptance>>();
const disableAcceptBtn = ref<boolean>(true);
const loading = ref<boolean>(false);
const apiService = new ApiService();

// Documents
const modalBodyMaxHeight = ref<string>();
const modalContent = ref<string>();
const showModal = ref<boolean>(false);
const contractDetails = ref<IContractData>();
const docModalTitle = ref<string>();
const contractTemplate = ref<string>();
const termsTemplate = ref<string>();

const onClose = () => {
  safeAndroidCall("showNav");
  safeAndroidCall("dismissWebView");
};

const openDocument = (docContent: "contract" | "termsConditions") => {
  try {
    if (contractDetails.value) {
      const country = summary.value?.country;
      const docService = DocFactoryService.create(
        country,
        contractDetails.value
      );

      if (docContent === "termsConditions") {
        docModalTitle.value = t("CL_CONTRACT.TYC_TITLE");
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickTyC,
          "Happy Path",
          "Click - T&C triggered"
        );
        modalContent.value = docService.buildTyC(termsTemplate.value ?? "");
      }

      if (docContent === "contract") {
        docModalTitle.value = t("CL_CONTRACT.CONTRACT_TITLE");
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickContract,
          "Happy Path",
          "Click - Contract triggered"
        );
        modalContent.value = docService.buildContract(
          contractTemplate.value ?? ""
        );
      }
      showModal.value = true;
    }
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error);
    modalContent.value =
      docContent === "contract"
        ? contractTemplate.value ?? ""
        : termsTemplate.value ?? "";
    showModal.value = true;
  }
};

const fetchContractDetails = async () => {
  try {
    const res = await apiService.getCreditLineContractsDetails({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      isLimitIncrease: true,
    });

    contractDetails.value = {
      ...res.data,
      annualPercentageRate: percentageFormat(res.data.annualPercentageRate),
      currentDate: getContractDate(),
    };
  } catch (err) {
    ErrorHandler.handleNetworkErrorAndLog(err);
    void router.replace({
      name: "CreditLineApiErrorDisplay",
      query: {
        title: t("ERROR_SOMETHING_GOES_WRONG"),
        description: err.response?.data?.message ?? "",
        returnHomeOnTryAgain: "true",
      },
    });
  }
};

const acceptLimitIncrease = async () => {
  loading.value = true;
  try {
    const signature = contractAcceptanceRef.value?.getSignature();
    if (!signature || signature.isEmpty) {
      loading.value = false;
      return;
    }

    const digitalSignature = signature.data.replace(
      "data:image/png;base64,",
      ""
    );

    logAnalyticsEvent(
      GoogleAnalyticsEventsHelper.CreditLine
        .JointLoanLineLimitIncreaseAcceptOfferClicked,
      "Super Pacman First Limit Increase"
    );

    await apiService.increaseCreditLimit({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      generateContract: true,
      digitalSignature,
    });

    void router.replace({ name: "CreditCardShippingAddressView" });
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error);
    void router.replace({
      name: "CreditLineApiErrorDisplay",
      query: {
        description:
          error?.response?.data?.message ?? t("ERROR_SOMETHING_GOES_WRONG"),
      },
    });
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  safeAndroidCall("hideNav");
  try {
    const settingsData = await getSettings(SettingsCategory.CL_REGISTRATION);
    contractTemplate.value =
      settingsData.creditLineContract?.values?.mobileVersion?.template;
    termsTemplate.value =
      settingsData.termsAndConditions?.values?.mobileVersion?.template;

    void fetchSummary();
    void fetchContractDetails();

    resizeComponentHelper(containerRef);
    modalBodyMaxHeight.value = getWindowHeightPercentage(65) + "px";
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error);
  }
  safeAndroidCall("finishedLoading");
});
</script>

<style lang="scss" scoped>
* {
  font-family: Inter, sans-serif;
}
.top-section {
  background: #f2f2f2;
  padding-bottom: 1rem;
}

h1 {
  color: var(--Typography-Text-2-ICON, #595a5a);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

h2 {
  color: var(--Typography-Text-3-sub, #8b8b8b);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

.new-amount {
  color: var(--Primary-Main-green, #007c3b);
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 40px;
  font-style: normal;
  font-weight: 600;
  line-height: 48px;
  letter-spacing: 0.18px;
}

.content-iframe {
  width: 100%;
  height: v-bind(modalBodyMaxHeight);
  border: none;
}
</style>
